<hlm-sheet side="right" [state]="store.isOpen() ? 'open' : 'closed'" (closed)="store.closeSheet()">
  <hlm-sheet-content *brnSheetContent="let ctx" class="flex w-[min(440px,100vw)] flex-col gap-0 p-0 sm:max-w-[440px]">
    <header class="shrink-0 border-b border-border px-5 pb-3 pr-12 pt-4">
      <p class="truncate font-mono text-2xs uppercase tracking-wide text-muted-foreground">{{ store.inbox.organizationName() || 'Keine Organisation' }}</p>
      <div class="mt-0.5 flex items-center justify-between gap-3">
        <h2 hlmSheetTitle class="text-lg font-bold">Aktivität</h2>
        <button
          hlmBtn
          variant="ghost"
          size="sm"
          class="-mr-2 h-8 gap-1.5 px-2 text-xs text-muted-foreground"
          [disabled]="!store.unreadCount()"
          (click)="store.inbox.markAllRead()"
        >
          <ng-icon name="lucideCheckCheck" class="size-4" />Alle gelesen
        </button>
      </div>
      <p hlmSheetDescription class="sr-only">Laufende Vorgänge, neue Anfragen und Ergebnisse</p>
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto">
      @if (store.active().length) {
        <section aria-labelledby="active-title" class="border-b border-border bg-muted/30 px-5 pb-2 pt-3">
          <h3 id="active-title" class="font-mono text-2xs uppercase tracking-wide text-muted-foreground">Gerade aktiv</h3>
          <ul>
            @for (item of store.active(); track item.id) {
              <li class="flex gap-3 py-2.5">
                @if (item.type === 'download') {
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground" aria-hidden="true">
                    <ng-icon [name]="asDownload(item).status === 'preparing' ? 'lucideLoaderCircle' : 'lucideDownload'" class="size-4" [class.animate-spin]="asDownload(item).status === 'preparing'" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm">{{ item.title }}</p>
                    <p class="text-xs" [class]="asDownload(item).status === 'failed' ? 'text-danger' : 'text-muted-foreground'">
                      {{ asDownload(item).status === 'ready' ? 'Bereit · nur bis zum Neuladen verfügbar' : asDownload(item).status === 'preparing' ? 'Wird vorbereitet …' : 'Download fehlgeschlagen' }}
                    </p>
                  </div>
                  @if (asDownload(item).status === 'ready') {
                    <button hlmBtn variant="outline" size="sm" class="h-8 shrink-0 gap-1.5 text-xs" (click)="triggerDownload(asDownload(item))">
                      <ng-icon name="lucideDownload" class="size-3.5" />Laden
                    </button>
                  }
                  @if (asDownload(item).status !== 'preparing') {
                    <button type="button" class="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40" [attr.aria-label]="'Entfernen: ' + item.title" (click)="store.remove(item.id)">
                      <ng-icon name="lucideX" class="size-4" />
                    </button>
                  }
                } @else {
                  @let job = asProgress(item);
                  <span
                    class="flex size-8 shrink-0 items-center justify-center rounded-full"
                    [class]="job.status === 'failed' ? 'bg-danger-soft text-danger' : job.status === 'complete' ? 'bg-success-soft text-success' : 'bg-primary/10 text-primary'"
                    aria-hidden="true"
                  >
                    <ng-icon [name]="job.status === 'failed' ? 'lucideTriangleAlert' : job.status === 'complete' ? 'lucideCheck' : 'lucideLoaderCircle'" class="size-4" [class.animate-spin]="job.status === 'in-progress'" />
                  </span>
                  <a
                    class="min-w-0 flex-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    [routerLink]="linkPath(job.routerLink)"
                    [queryParams]="linkQuery(job.routerLink)"
                    (click)="store.closeSheet()"
                  >
                    <span class="flex items-baseline justify-between gap-3">
                      <span class="truncate text-sm hover:underline">{{ job.title }}</span>
                      <time class="shrink-0 text-2xs text-muted-foreground">{{ formatTime(job.finishedAt ?? job.timestamp) }}</time>
                    </span>
                    <span class="block truncate text-xs" [class]="job.status === 'failed' ? 'text-danger' : 'text-muted-foreground'">{{ job.body || (job.status === 'in-progress' ? 'Wird ausgeführt …' : job.status === 'failed' ? 'Fehlgeschlagen' : 'Abgeschlossen') }}</span>
                    @if (job.status === 'in-progress') {
                      <span
                        class="mt-2 block h-1 overflow-hidden rounded-full bg-border"
                        role="progressbar"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        [attr.aria-valuenow]="job.progress ?? null"
                        [attr.aria-label]="'Fortschritt: ' + job.title"
                      >
                        @if (job.progress !== undefined) {
                          <span class="block h-full rounded-full bg-primary transition-[width] duration-500" [style.width.%]="job.progress"></span>
                        } @else {
                          <span class="block h-full w-1/3 animate-pulse rounded-full bg-primary/60"></span>
                        }
                      </span>
                    }
                  </a>
                }
              </li>
            }
          </ul>
        </section>
      }

      <div class="flex items-center gap-1 px-5 pb-1 pt-3">
        <div role="group" aria-label="Filter" class="inline-flex gap-1 text-xs">
          @for (filter of filters; track filter.unread) {
            <button
              type="button"
              class="rounded-md px-2.5 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              [class]="store.inbox.unreadOnly() === filter.unread ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'"
              [attr.aria-pressed]="store.inbox.unreadOnly() === filter.unread"
              (click)="store.inbox.filterUnread(filter.unread)"
            >
              {{ filter.label }}@if (filter.unread && store.unreadCount()) { <span class="ml-1 font-mono">{{ store.unreadCount() }}</span> }
            </button>
          }
        </div>
      </div>

      @if (store.inbox.error()) {
        <div class="mx-5 my-2 flex items-center gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">
          <span class="min-w-0 flex-1">{{ store.inbox.error() }}</span>
          <button hlmBtn variant="ghost" size="sm" class="h-7 text-xs" (click)="store.inbox.refresh()">Erneut versuchen</button>
        </div>
      } @else if (store.inbox.reconnecting()) {
        <p role="status" class="px-5 py-1 text-xs text-muted-foreground">Verbindung wird wiederhergestellt …</p>
      }

      @for (day of days(); track day.label) {
        <section [attr.aria-label]="day.label">
          <h3 class="px-5 pb-1 pt-3 font-mono text-2xs uppercase tracking-wide text-muted-foreground">{{ day.label }}</h3>
          <ul>
            @for (item of day.items; track item.id) {
              @let category = look(item);
              <li class="group relative flex gap-3 px-5 py-3 transition-colors hover:bg-muted/40" [class]="item.read ? '' : 'bg-primary/5'">
                <span class="flex size-8 shrink-0 items-center justify-center rounded-full" [class]="category.tone" aria-hidden="true">
                  <ng-icon [name]="category.icon" class="size-4" />
                </span>
                <a
                  class="min-w-0 flex-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                  [routerLink]="linkPath(item.routerLink)"
                  [queryParams]="linkQuery(item.routerLink)"
                  (click)="open(item)"
                >
                  <span class="flex items-baseline justify-between gap-3">
                    <span class="truncate text-sm" [class.font-bold]="!item.read">{{ item.title }}<span class="sr-only">{{ item.read ? '' : ' (ungelesen)' }}</span></span>
                    <time class="shrink-0 text-2xs text-muted-foreground" [attr.datetime]="item.timestamp.toISOString()">{{ formatTime(item.timestamp) }}</time>
                  </span>
                  @if (item.address || item.sourceName) {
                    <span class="block truncate text-xs text-muted-foreground">{{ item.address || item.sourceName }}@if (item.address && item.sourceName) { · {{ item.sourceName }} }</span>
                  }
                  @if (item.body) {
                    <span class="mt-1 block text-xs" [class]="item.category === 'execution_failed' ? 'text-danger' : 'text-foreground/80'">{{ item.body }}</span>
                  }
                </a>
                <span class="flex w-8 shrink-0 flex-col items-center">
                  <button
                    type="button"
                    class="flex size-8 items-center justify-center rounded-md text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 group-hover:opacity-100"
                    [attr.aria-label]="(item.read ? 'Als ungelesen markieren: ' : 'Als gelesen markieren: ') + item.title"
                    [title]="item.read ? 'Als ungelesen markieren' : 'Als gelesen markieren'"
                    (click)="store.inbox.markRead(item.id, !item.read)"
                  >
                    <ng-icon [name]="item.read ? 'lucideMailOpen' : 'lucideCheck'" class="size-4" />
                  </button>
                  @if (!item.read) {
                    <span class="-mt-5 size-2 rounded-full bg-primary group-hover:invisible" aria-hidden="true"></span>
                  }
                </span>
              </li>
            }
          </ul>
        </section>
      } @empty {
        @if (!store.inbox.error()) {
          <div class="flex flex-col items-center px-8 py-14 text-center" role="status">
            @if (store.inbox.busy()) {
              <ng-icon name="lucideLoaderCircle" class="size-5 animate-spin text-muted-foreground" />
              <p class="mt-3 text-sm text-muted-foreground">Wird geladen …</p>
            } @else {
              <span class="flex size-10 items-center justify-center rounded-full bg-secondary text-muted-foreground"><ng-icon name="lucideBell" class="size-5" /></span>
              <p class="mt-3 text-sm font-bold">{{ store.inbox.unreadOnly() ? 'Alles gelesen' : 'Noch nichts Neues' }}</p>
              <p class="mt-1 text-xs text-muted-foreground">Neue Anfragen, Ergebnisse und Änderungen an Ihrem Zugang erscheinen hier.</p>
            }
          </div>
        }
      }

      @if (store.inbox.hasMore()) {
        <div class="p-4 text-center">
          <button hlmBtn variant="ghost" size="sm" [disabled]="store.inbox.busy()" (click)="store.inbox.loadMore()">Ältere laden</button>
        </div>
      }
    </div>

    <footer class="shrink-0 border-t border-border px-5 py-3">
      <a routerLink="/user/settings" [queryParams]="{ tab: 'notifications' }" class="text-sm text-primary underline underline-offset-4" (click)="store.closeSheet()">Benachrichtigungen einstellen</a>
    </footer>
  </hlm-sheet-content>
</hlm-sheet>
